const ZIP_END_SIGNATURE = 0x06054b50;
const ZIP_CENTRAL_SIGNATURE = 0x02014b50;
const ZIP_LOCAL_SIGNATURE = 0x04034b50;

const toArrayBuffer = async (data) => {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) return data.arrayBuffer();
  throw new Error("The downloaded document is not a supported binary response.");
};

const findEndOfCentralDirectory = (view) => {
  const minimumOffset = Math.max(0, view.byteLength - 65557);
  for (let offset = view.byteLength - 22; offset >= minimumOffset; offset -= 1) {
    if (view.getUint32(offset, true) === ZIP_END_SIGNATURE) return offset;
  }
  throw new Error("The downloaded file is not a valid DOCX document.");
};

const inflateRaw = async (compressedData) => {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser cannot decompress DOCX files for preview.");
  }

  const stream = new Blob([compressedData])
    .stream()
    .pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

const readZipEntry = async (arrayBuffer, targetName) => {
  const view = new DataView(arrayBuffer);
  const bytes = new Uint8Array(arrayBuffer);
  const decoder = new TextDecoder("utf-8");
  const endOffset = findEndOfCentralDirectory(view);
  const entryCount = view.getUint16(endOffset + 10, true);
  let offset = view.getUint32(endOffset + 16, true);

  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(offset, true) !== ZIP_CENTRAL_SIGNATURE) break;

    const flags = view.getUint16(offset + 8, true);
    const compressionMethod = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const fileNameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const fileName = decoder.decode(bytes.slice(offset + 46, offset + 46 + fileNameLength));

    if (fileName === targetName) {
      if ((flags & 0x1) !== 0) throw new Error("Encrypted DOCX files cannot be previewed.");
      if (view.getUint32(localHeaderOffset, true) !== ZIP_LOCAL_SIGNATURE) {
        throw new Error("The DOCX document contains an invalid ZIP entry.");
      }

      const localNameLength = view.getUint16(localHeaderOffset + 26, true);
      const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
      const dataOffset = localHeaderOffset + 30 + localNameLength + localExtraLength;
      const compressedData = bytes.slice(dataOffset, dataOffset + compressedSize);

      if (compressionMethod === 0) return compressedData;
      if (compressionMethod === 8) return inflateRaw(compressedData);
      throw new Error("This DOCX file uses an unsupported compression method.");
    }

    offset += 46 + fileNameLength + extraLength + commentLength;
  }

  throw new Error("The DOCX document does not contain readable document content.");
};

const readNodeText = (node) => {
  let text = "";
  Array.from(node.childNodes || []).forEach((child) => {
    if (child.localName === "t") text += child.textContent || "";
    else if (child.localName === "tab") text += "\t";
    else if (child.localName === "br" || child.localName === "cr") text += "\n";
    else text += readNodeText(child);
  });
  return text;
};

const directChildren = (node, localName) =>
  Array.from(node?.children || []).filter((child) => child.localName === localName);

const parseDocumentXml = (xmlText) => {
  const documentXml = new DOMParser().parseFromString(xmlText, "application/xml");
  if (documentXml.getElementsByTagName("parsererror").length > 0) {
    throw new Error("The DOCX document XML could not be read.");
  }

  const body = Array.from(documentXml.documentElement.children)
    .find((child) => child.localName === "body");
  if (!body) throw new Error("The DOCX document has no readable body.");

  const blocks = [];
  Array.from(body.children).forEach((child) => {
    if (child.localName === "p") {
      blocks.push({ type: "paragraph", text: readNodeText(child) });
      return;
    }

    if (child.localName === "tbl") {
      const rows = directChildren(child, "tr").map((row) =>
        directChildren(row, "tc").map((cell) =>
          directChildren(cell, "p").map(readNodeText).join("\n")
        )
      );
      blocks.push({ type: "table", rows });
    }
  });

  return blocks;
};

export const extractDocxPreview = async (data) => {
  const arrayBuffer = await toArrayBuffer(data);
  const documentXml = await readZipEntry(arrayBuffer, "word/document.xml");
  return parseDocumentXml(new TextDecoder("utf-8").decode(documentXml));
};
